import { Check, ChevronDown, X } from 'lucide-react';
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface CountryEntry {
  /** ISO 3166-1 alpha-2 code, lowercased (matches `vessel.flag`). */
  code: string;
  /** Localized common name in the user's preferred language when
   *  available, falling back to English. */
  name: string;
  /** Unicode flag emoji. */
  emoji: string;
}

interface FlagDropdownProps {
  /** Current ISO alpha-2 code (lowercase) or null when unset. */
  value: string | null;
  onChange: (code: string | null) => void;
  /** Show a clear button when a value is selected. */
  clearable?: boolean;
  disabled?: boolean;
  className?: string;
  /** Forwarded to the input for label association. */
  id?: string;
}

/**
 * FlagDropdown — country picker with searchable dropdown + flag
 * emoji + ISO alpha-2 storage.
 *
 * The `world-countries` package (~250 KB minified) is loaded lazily
 * the first time the component mounts: a dynamic `import()` puts it
 * in its own vendor chunk that only ships when an admin opens the
 * vessel form. Initial render shows a loading state.
 *
 * Filter strategy: lowercase substring match on (name, code).
 * Keyboard nav: ArrowDown/Up moves the highlighted index, Enter
 * selects, Escape closes. Click-outside also closes.
 */
export function FlagDropdown({
  value,
  onChange,
  clearable = true,
  disabled,
  className,
  id,
}: FlagDropdownProps) {
  const { t, i18n } = useTranslation();
  const inputId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [allCountries, setAllCountries] = useState<CountryEntry[] | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);

  // Lazy-load the world-countries dataset on mount. Pulls in a
  // separate vendor chunk so the home dashboard doesn't pay for it.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const mod = await import('world-countries');
      if (cancelled) return;
      const langKey =
        i18n.language === 'fr'
          ? 'fra'
          : i18n.language === 'es'
            ? 'spa'
            : null;
      const list: CountryEntry[] = mod.default
        .map((c) => {
          const localizedName =
            (langKey && (c.translations[langKey]?.common as string)) ||
            (c.name.common as string);
          return {
            code: (c.cca2 as string).toLowerCase(),
            name: localizedName,
            emoji: c.flag as string,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
      setAllCountries(list);
    })();
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  // Click-outside closes the dropdown.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = useMemo(() => {
    if (!value || !allCountries) return null;
    const lc = value.toLowerCase();
    return allCountries.find((c) => c.code === lc) ?? null;
  }, [value, allCountries]);

  const filtered = useMemo(() => {
    if (!allCountries) return [];
    const q = query.trim().toLowerCase();
    if (!q) return allCountries;
    return allCountries.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.includes(q),
    );
  }, [query, allCountries]);

  // Keep highlight in range when filter narrows.
  useEffect(() => {
    if (highlighted >= filtered.length) setHighlighted(0);
  }, [filtered.length, highlighted]);

  const select = (entry: CountryEntry) => {
    onChange(entry.code);
    setQuery('');
    setOpen(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const entry = filtered[highlighted];
      if (entry) select(entry);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const placeholder = allCountries
    ? t('vessel.form.flagSearch')
    : t('common.loading');

  return (
    <div
      ref={wrapperRef}
      className={cn('relative', className)}
    >
      <div
        className={cn(
          'flex items-center gap-2 rounded-md border border-input bg-background px-3 text-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        {selected ? (
          <span aria-hidden className="text-base">
            {selected.emoji}
          </span>
        ) : null}
        <input
          ref={inputRef}
          id={id ?? inputId}
          type="text"
          autoComplete="off"
          disabled={disabled || !allCountries}
          placeholder={selected ? `${selected.name} (${selected.code.toUpperCase()})` : placeholder}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${inputId}-listbox`}
          aria-autocomplete="list"
          className="h-10 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
        />
        {clearable && selected && !disabled ? (
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setQuery('');
              inputRef.current?.focus();
            }}
            aria-label={t('common.clear')}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
        <ChevronDown
          className="h-4 w-4 text-muted-foreground"
          aria-hidden
        />
      </div>

      {open && allCountries && filtered.length > 0 ? (
        <ul
          id={`${inputId}-listbox`}
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover p-1 text-sm shadow-md"
        >
          {filtered.slice(0, 100).map((c, idx) => {
            const isSelected = c.code === value?.toLowerCase();
            const isHighlighted = idx === highlighted;
            return (
              <li
                key={c.code}
                role="option"
                aria-selected={isSelected}
                onMouseDown={(e) => {
                  // mousedown so it fires before the input blur
                  e.preventDefault();
                  select(c);
                }}
                onMouseEnter={() => setHighlighted(idx)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5',
                  isHighlighted && 'bg-accent text-accent-foreground',
                )}
              >
                <span aria-hidden className="text-base">
                  {c.emoji}
                </span>
                <span className="flex-1 truncate">{c.name}</span>
                <span className="font-mono text-xs uppercase text-muted-foreground">
                  {c.code}
                </span>
                {isSelected ? <Check className="h-4 w-4" aria-hidden /> : null}
              </li>
            );
          })}
          {filtered.length > 100 ? (
            <li className="px-2 py-1.5 text-center text-xs text-muted-foreground">
              {t('vessel.form.flagMoreResults', { count: filtered.length - 100 })}
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
